// Éditeur de ligne — modèle PUR (aucun DOM), testé dans test/editor.test.mjs.
//
// Une ligne est une liste de jetons ; une fraction est UN jeton qui contient deux listes :
//   { k:'num', v:'12' } { k:'id', v:'x' } { k:'op', v:'+' } { k:'lp' } { k:'rp' }
//   { k:'frac', num:[…], den:[…] }
// Un conteneur est désigné par un chemin : []                → la ligne
//                                           [3, 'num']       → le numérateur du jeton 3
//                                           [3, 'den', 0, 'num'] → fraction imbriquée
// Curseur : { c: chemin, i: indice d'insertion }. Sélection : { c, from, to } (to exclu), dans UN conteneur.

const BIN_ADD = new Set(['+', '−']);
const BIN_MUL = new Set(['×', '÷']);
const RELS = new Set(['=', '≤', '≥', '<', '>', '≠', '≈', 'ou', 'et']);
// ′ : marque de dérivée (u)′, postfixe comme ² et ³
// ⁺ ⁻ : côté d'approche (3⁺, 0⁻) ; ! : factorielle (8!, (n + 1)!) ; ° : degrés (tan(35°))
const POSTFIX = new Set(['²', '³', '′', '⁺', '⁻', '!', '°']);

export const clone = (x) => JSON.parse(JSON.stringify(x));

export function createEditor(tokens = []) {
  return { tokens: clone(tokens), cursor: { c: [], i: tokens.length }, sel: null, undo: [] };
}

// ── Accès aux conteneurs ──────────────────────────────────────────────────
export function container(state, path) {
  let arr = state.tokens;
  for (let j = 0; j < path.length; j += 2) arr = arr[path[j]][path[j + 1]];
  return arr;
}
const samePath = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

function snapshot(state) {
  state.undo.push(clone({ tokens: state.tokens, cursor: state.cursor, sel: state.sel }));
  if (state.undo.length > 200) state.undo.shift();
}
export function undo(state) {
  const prev = state.undo.pop();
  if (!prev) return false;
  Object.assign(state, prev, { undo: state.undo });
  return true;
}

// ── Lecture : unaire ou binaire, unités, groupes ──────────────────────────
// Le « − » est unaire en tête de conteneur, après « ( », après un opérateur ou une relation.
export function isUnary(arr, i) {
  const t = arr[i];
  if (!t || t.k !== 'op' || (t.v !== '−' && t.v !== '+')) return false;
  const p = arr[i - 1];
  return !p || p.k === 'lp' || (p.k === 'op' && !POSTFIX.has(p.v));
}

export function matchParen(arr, i) {
  const t = arr[i];
  if (t?.k === 'lp') {
    let d = 0;
    for (let j = i; j < arr.length; j++) {
      if (arr[j].k === 'lp') d++;
      if (arr[j].k === 'rp' && --d === 0) return j;
    }
  }
  if (t?.k === 'rp') {
    let d = 0;
    for (let j = i; j >= 0; j--) {
      if (arr[j].k === 'rp') d++;
      if (arr[j].k === 'lp' && --d === 0) return j;
    }
  }
  return -1;
}

// Une unité = nombre, lettre, fraction ou groupe parenthésé, avec ses exposants collés (², ³, ^(…)).
// Renvoie [début, fin exclue] de l'unité qui se termine en `end - 1`, ou qui commence en `start`.
function unitEndingAt(arr, last) {
  let j = last;
  while (j >= 0 && arr[j].k === 'op' && POSTFIX.has(arr[j].v)) j--;
  if (j >= 1 && arr[j].k === 'rp') {
    const o = matchParen(arr, j);
    if (o >= 1 && arr[o - 1].k === 'op' && arr[o - 1].v === '^') j = o - 2; // a^(…)
  }
  if (j < 0) return null;
  if (arr[j].k === 'rp') {
    const o = matchParen(arr, j);
    return o < 0 ? null : o;
  }
  if (['num', 'id', 'frac'].includes(arr[j].k)) return j;
  return null;
}
function unitStartingAt(arr, first) {
  let j = first;
  if (j >= arr.length) return null;
  if (arr[j].k === 'op' && arr[j].v === '−' && isUnary(arr, j)) j++; // « × −4 »
  if (j >= arr.length) return null;
  if (arr[j].k === 'lp') {
    const c = matchParen(arr, j);
    if (c < 0) return null;
    j = c;
  } else if (!['num', 'id', 'frac'].includes(arr[j].k)) return null;
  j++;
  for (;;) {
    if (arr[j]?.k === 'op' && POSTFIX.has(arr[j].v)) {
      j++;
      continue;
    }
    if (arr[j]?.k === 'op' && arr[j].v === '^' && arr[j + 1]?.k === 'lp') {
      const c = matchParen(arr, j + 1);
      if (c > 0) {
        j = c + 1;
        continue;
      }
    }
    break;
  }
  return j;
}

// Double-clic : × ou ÷ → l'opération locale et ses deux opérandes ; + ou − binaire → les termes voisins ;
// parenthèse → le groupe ; « − » unaire → le signe et son unité ; autre jeton → lui-même.
export function smartRange(arr, i) {
  const t = arr[i];
  if (!t) return null;
  if (t.k === 'lp' || t.k === 'rp') {
    const m = matchParen(arr, i);
    return m < 0 ? [i, i + 1] : [Math.min(i, m), Math.max(i, m) + 1];
  }
  if (t.k === 'op' && BIN_MUL.has(t.v)) {
    const a = unitEndingAt(arr, i - 1),
      b = unitStartingAt(arr, i + 1);
    return [a ?? i, b ?? i + 1];
  }
  if (t.k === 'op' && BIN_ADD.has(t.v)) {
    if (isUnary(arr, i)) {
      const b = unitStartingAt(arr, i + 1);
      return [i, b ?? i + 1];
    }
    return [termStart(arr, i - 1), termEnd(arr, i + 1)];
  }
  return [i, i + 1];
}
const stopsTerm = (t) => t.k === 'op' && (RELS.has(t.v) || BIN_ADD.has(t.v));
function termStart(arr, j) {
  let d = 0;
  for (; j >= 0; j--) {
    const t = arr[j];
    if (t.k === 'rp') d++;
    else if (t.k === 'lp') {
      if (d === 0) return j + 1;
      d--;
    } else if (d === 0 && stopsTerm(t) && !isUnary(arr, j)) return j + 1;
  }
  return 0;
}
function termEnd(arr, j) {
  let d = 0;
  for (; j < arr.length; j++) {
    const t = arr[j];
    if (t.k === 'lp') d++;
    else if (t.k === 'rp') {
      if (d === 0) return j;
      d--;
    } else if (d === 0 && stopsTerm(t) && !isUnary(arr, j)) return j;
  }
  return arr.length;
}

// ── Sélection et curseur ──────────────────────────────────────────────────
export function select(state, c, from, to = from + 1) {
  const arr = container(state, c);
  if (!arr.length) {
    state.sel = null;
    state.cursor = { c, i: 0 };
    return;
  }
  from = Math.max(0, Math.min(from, arr.length - 1));
  to = Math.max(from + 1, Math.min(to, arr.length));
  state.sel = { c, from, to };
  state.cursor = { c, i: to };
}
export function selectSmart(state, c, i) {
  const r = smartRange(container(state, c), i);
  if (r) select(state, c, r[0], r[1]);
}
export function placeCursor(state, c, i) {
  state.sel = null;
  state.cursor = { c, i };
}
// Appui long sur une sélection : curseur d'INSERTION juste avant le bloc, montré comme une case « ? ». Les touches
// écrivent là ; toute autre action qui replace le curseur (toucher ailleurs) le fait disparaître.
export function insertBefore(state) {
  if (!state.sel) return false;
  state.cursor = { c: state.sel.c, i: state.sel.from, slot: true };
  state.sel = null;
  return true;
}
export function selectedFraction(state) {
  const s = state.sel;
  if (!s || s.to - s.from !== 1) return null;
  const t = container(state, s.c)[s.from];
  return t?.k === 'frac' ? { path: [...s.c, s.from], token: t } : null;
}

// ── Édition ───────────────────────────────────────────────────────────────
// key : { k:'num', v:'7' } chiffre (prolonge le nombre en cours), { k:'num', v:'12' } nombre entier,
//       { k:'id', v:'x' }, { k:'op', v:'+' }, { k:'lp' }, { k:'rp' }, { k:'comma' },
//       { k:'frac' } touche des bouts : □/□ vide (curseur en haut), puis descend au dénominateur ;
//       { k:'frac', capture: true } touche « / » du clavier : l'unité avant le curseur (ou la sélection)
//       devient le numérateur.
export function press(state, key) {
  snapshot(state);
  if (state.cursor.slot) state.cursor = { c: state.cursor.c, i: state.cursor.i }; // la case « ? » se remplit
  if (key.k === 'frac') return makeFraction(state, key);
  if (state.sel) {
    state.cursor = { c: state.sel.c, i: state.sel.to };
    state.sel = null;
  }
  leaveFractionIfNeeded(state, key);
  const { c, i } = state.cursor;
  const arr = container(state, c);
  const prev = arr[i - 1];
  if (key.k === 'digit' || key.k === 'comma') {
    const ch = key.k === 'comma' ? ',' : key.v;
    if (prev?.k === 'num' && !(ch === ',' && prev.v.includes(','))) {
      prev.v += ch;
      return;
    }
    if (ch === ',') {
      arr.splice(i, 0, { k: 'num', v: '0,' });
      state.cursor.i++;
      return;
    }
    arr.splice(i, 0, { k: 'num', v: ch });
    state.cursor.i++;
    return;
  }
  // « ^ » ouvre une case d'exposant : ^( ) avec le curseur dedans
  if (key.k === 'op' && key.v === '^') {
    arr.splice(i, 0, { k: 'op', v: '^' }, { k: 'lp' }, { k: 'rp' });
    state.cursor.i = i + 2;
    return;
  }
  // « ) » juste avant une parenthèse fermante déjà là (case d'exposant ^( ), parenthèses posées par un outil) : on la
  // franchit au lieu d'en ajouter une — c'est ainsi qu'on sort de l'exposant de p^k
  if (key.k === 'rp' && arr[i]?.k === 'rp') {
    state.cursor.i++;
    return;
  }
  // « ) » au bout de l'expression d'une limite lim(… ; a) (juste avant son « ; ») : on sort de la limite
  if (key.k === 'rp' && arr[i]?.k === 'op' && arr[i].v === ';') {
    for (let j = i - 1, d = 0; j >= 0; j--) {
      if (arr[j].k === 'rp') d++;
      else if (arr[j].k === 'lp') {
        // la première parenthèse non fermée doit être celle de l'appel lim( : sinon c'est une parenthèse intérieure à fermer
        if (d === 0) {
          const m = matchParen(arr, j);
          if (arr[j - 1]?.k === 'id' && m > i) {
            state.cursor.i = m + 1;
            return;
          }
          break;
        }
        d--;
      }
    }
  }
  // « ; » juste avant le « ; » d'un coefficient binomial C( ; ) : on passe à la case du bas
  if (key.k === 'op' && key.v === ';' && arr[i]?.k === 'op' && arr[i].v === ';') {
    state.cursor.i++;
    return;
  }
  // « ( » juste après « ^( » vide : la case est déjà ouverte
  if (
    key.k === 'lp' &&
    prev?.k === 'lp' &&
    arr[i - 2]?.k === 'op' &&
    arr[i - 2].v === '^' &&
    arr[i]?.k === 'rp'
  )
    return;
  // une touche nombre juste après un nombre le PROLONGE (« 2 » puis « 5 » → 25) : deux nombres côte à côte n'ont
  // pas de sens, et l'élève compose un nombre nouveau avec les nombres déjà écrits comme avec des chiffres
  if (key.k === 'num' && prev?.k === 'num' && /^\d+$/.test(key.v)) {
    prev.v += key.v;
    return;
  }
  arr.splice(i, 0, clone(key));
  state.cursor.i++;
}

// Au bout d'un dénominateur déjà rempli, « + », « − » binaire, une relation ou une parenthèse fermante
// sans ouvrante font sortir de la fraction : « 10/15 + 12/15 » se tape d'un trait.
// Pour une somme au dénominateur, on ouvre une parenthèse (ou on encadre).
function leaveFractionIfNeeded(state, key) {
  for (;;) {
    const { c, i } = state.cursor;
    if (!c.length || c[c.length - 1] !== 'den') return; // en haut, c'est la touche ↓ qui fait descendre
    const arr = container(state, c);
    if (i !== arr.length || !arr.length) return;
    let open = 0;
    for (const t of arr) {
      if (t.k === 'lp') open++;
      else if (t.k === 'rp') open--;
    }
    const exits =
      (key.k === 'op' && (BIN_ADD.has(key.v) || RELS.has(key.v)) && open === 0) ||
      (key.k === 'rp' && open === 0);
    if (!exits) return;
    const parent = c.slice(0, -2),
      idx = c[c.length - 2];
    state.cursor = { c: parent, i: idx + 1 };
  }
}

// Où est le curseur ? 'num' | 'den' | null (hors fraction).
export function cursorSlot(state) {
  const c = state.sel ? state.sel.c : state.cursor.c;
  return c.length ? c[c.length - 1] : null;
}

function makeFraction(state, key) {
  let c, from, to;
  // touche des bouts (fraction à deux cases) : dans un numérateur, elle descend au dénominateur…
  if (!key.capture && !key.nest && !state.sel && cursorSlot(state) === 'num') {
    const fracPath = state.cursor.c.slice(0, -1);
    state.cursor = { c: [...fracPath, 'den'], i: container(state, [...fracPath, 'den']).length };
    return;
  }
  // … sinon elle pose une fraction vide □/□, curseur en haut.
  if (!key.capture && !state.sel) {
    c = state.cursor.c;
    const arr = container(state, c);
    arr.splice(state.cursor.i, 0, { k: 'frac', num: [], den: [] });
    state.cursor = { c: [...c, state.cursor.i, 'num'], i: 0 };
    return;
  }
  if (state.sel) ({ c, from, to } = state.sel);
  else {
    c = state.cursor.c;
    const arr = container(state, c);
    to = state.cursor.i;
    const start = to > 0 ? unitEndingAt(arr, to - 1) : null;
    from = start ?? to;
  }
  const arr = container(state, c);
  let num = arr.slice(from, to);
  if (num.length > 2 && num[0].k === 'lp' && matchParen(num, 0) === num.length - 1)
    num = num.slice(1, -1); // (a + b)/… → numérateur a + b
  const frac = { k: 'frac', num, den: [] };
  arr.splice(from, to - from, frac);
  state.sel = null;
  state.cursor = { c: [...c, from, num.length ? 'den' : 'num'], i: 0 };
}

export function wrapParens(state) {
  if (!state.sel) return false;
  snapshot(state);
  const { c, from, to } = state.sel;
  const arr = container(state, c);
  arr.splice(to, 0, { k: 'rp' });
  arr.splice(from, 0, { k: 'lp' });
  select(state, c, from, to + 2);
  return true;
}

// Supprimer : la sélection, sinon le jeton avant le curseur (un chiffre à la fois dans un nombre).
// Ensuite la sélection va sur le symbole précédent, sinon sur le premier restant ; ligne vide → aucune.
export function erase(state) {
  snapshot(state);
  if (state.cursor.slot) state.cursor = { c: state.cursor.c, i: state.cursor.i };
  let c, from;
  if (state.sel) {
    ({ c, from } = state.sel);
    container(state, c).splice(from, state.sel.to - from);
  } else {
    ({ c } = state.cursor);
    const arr = container(state, c);
    const i = state.cursor.i;
    if (i === 0) {
      if (c.length) {
        // au début d'un numérateur / dénominateur : on sort de la fraction
        const parent = c.slice(0, -2),
          idx = c[c.length - 2];
        const frac = container(state, parent)[idx];
        if (!frac.num.length && !frac.den.length) container(state, parent).splice(idx, 1);
        state.sel = null;
        state.cursor = { c: parent, i: frac.num.length || frac.den.length ? idx + 1 : idx };
        return;
      }
      state.undo.pop();
      return;
    }
    const t = arr[i - 1];
    if (t.k === 'num' && t.v.length > 1) {
      t.v = t.v.slice(0, -1);
      return;
    }
    arr.splice(i - 1, 1);
    from = i - 1;
  }
  const arr = container(state, c);
  if (!arr.length && !c.length) {
    state.sel = null;
    state.cursor = { c, i: 0 };
    return;
  }
  if (!arr.length) {
    state.sel = null;
    state.cursor = { c, i: 0 };
    return;
  }
  const target = from > 0 ? from - 1 : 0;
  if (state.sel) select(state, c, target, target + 1);
  else state.cursor = { c, i: from };
}

export function clear(state) {
  snapshot(state);
  state.tokens = [];
  state.sel = null;
  state.cursor = { c: [], i: 0 };
}
export function load(state, tokens) {
  snapshot(state);
  state.tokens = clone(tokens);
  state.sel = null;
  state.cursor = { c: [], i: state.tokens.length };
}

// ── Facteur multiplicateur sur une fraction (§6 des specs) ────────────────
// Accepte un entier positif « 5 », ou un produit parenthésé simple « (3 × 2) ».
export function parseFactor(text) {
  const s = text.trim().replace(/\*/g, '×').replace(/x/g, '×');
  if (/^\d+$/.test(s)) return +s > 0 ? [{ k: 'num', v: String(+s) }] : null;
  const m = /^\(\s*(\d+)\s*×\s*(\d+)\s*\)$/.exec(s);
  if (m && +m[1] > 0 && +m[2] > 0)
    return [
      { k: 'lp' },
      { k: 'num', v: m[1] },
      { k: 'op', v: '×' },
      { k: 'num', v: m[2] },
      { k: 'rp' },
    ];
  return null;
}
export function factorMessage(text) {
  const s = text.trim();
  if (!s) return 'Écris le facteur : un entier positif, par exemple 5.';
  if (/^-|^−/.test(s)) return 'Le facteur doit être positif : le signe reste devant la fraction.';
  if (/[,.]/.test(s)) return 'Le facteur doit être un entier, par exemple 5.';
  return `Je ne reconnais pas « ${s} » : écris un entier positif, par exemple 5, ou un produit comme (3 × 2).`;
}
// « × k » en haut et en bas. Un côté simple devient (a × k) — l'écriture du corrigé, (2 × 5)/(3 × 5) — ; un côté
// qui est une SOMME garde ses parenthèses : (y − p) × 6, jamais (y − p × 6) qui voudrait dire y − 6p.
function isSumSide(side) {
  let d = 0;
  for (let j = 0; j < side.length; j++) {
    const t = side[j];
    if (t.k === 'lp') d++;
    else if (t.k === 'rp') d--;
    else if (d === 0 && t.k === 'op' && (BIN_ADD.has(t.v) || RELS.has(t.v)) && !isUnary(side, j))
      return true;
  }
  return false;
}
export function scaledFraction(frac, factor) {
  const times = (side) =>
    isSumSide(side)
      ? [{ k: 'lp' }, ...clone(side), { k: 'rp' }, { k: 'op', v: '×' }, ...clone(factor)]
      : [{ k: 'lp' }, ...clone(side), { k: 'op', v: '×' }, ...clone(factor), { k: 'rp' }];
  return { k: 'frac', num: times(frac.num), den: times(frac.den) };
}
export function applyFactor(state, text) {
  const f = selectedFraction(state);
  const factor = parseFactor(text);
  if (!f || !factor)
    return {
      ok: false,
      message: f ? factorMessage(text) : 'Sélectionne d’abord une fraction de la ligne.',
    };
  if (!f.token.num.length || !f.token.den.length)
    return { ok: false, message: 'Complète d’abord le numérateur et le dénominateur.' };
  snapshot(state);
  const c = f.path.slice(0, -1),
    idx = f.path[f.path.length - 1];
  container(state, c)[idx] = scaledFraction(f.token, factor);
  select(state, c, idx, idx + 1); // la fraction transformée reste sélectionnée
  return { ok: true };
}

// ── Texte pour le moteur ──────────────────────────────────────────────────
// Chaque fraction est écrite entre parenthèses : ((2 × 5) ÷ (3 × 5)) — voir CONTRACT §8.
export function toEngineText(tokens) {
  const out = [];
  for (const t of tokens) {
    if (t.k === 'frac') out.push(`((${toEngineText(t.num)}) ÷ (${toEngineText(t.den)}))`);
    else if (t.k === 'sign') out.push(' ? ');
    else if (t.k === 'lp') out.push('(');
    else if (t.k === 'rp') out.push(')');
    else if (t.k === 'op' && POSTFIX.has(t.v)) out.push(t.v);
    else
      out.push(
        t.k === 'op'
          ? ` ${t.v} `
          : (out.length && /[0-9a-zA-Zα-ω₀-₉)]$/u.test(out[out.length - 1]) ? ' ' : '') + t.v,
      );
  }
  return (
    out
      .join('')
      .replace(/\s+/g, ' ')
      .replace(/\( /g, '(')
      .replace(/ \)/g, ')')
      // « e^y », pas « e ^ y » : certains lecteurs (dérivées partielles) comparent la réponse à l'écriture près
      .replace(/ ?\^ ?/g, '^')
      .trim()
  );
}
export function emptySlot(tokens, path = []) {
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (t.k === 'sign')
      return { path, i, sign: true, msg: 'Choisis d’abord le signe de chaque terme (cases ±).' };
    if (t.k !== 'frac') continue;
    if (!t.num.length)
      return { path: [...path, i, 'num'], msg: 'Il manque le numérateur d’une fraction.' };
    if (!t.den.length)
      return { path: [...path, i, 'den'], msg: 'Il manque le dénominateur d’une fraction.' };
    const inner = emptySlot(t.num, [...path, i, 'num']) || emptySlot(t.den, [...path, i, 'den']);
    if (inner) return inner;
  }
  return null;
}

/** Un nom de plusieurs lettres (« MN », « AB », « CMₘ ») : ni un indice, ni un accent ne comptent. */
function isLongName(name) {
  return (String(name).match(/[\p{Lu}\p{Ll}\p{Lo}]/gu) ?? []).length > 1;
}

// ── AST du moteur → jetons (lignes du corrigé, recopie, coup de pouce) ────
// Ne lit que la forme des nœuds et la stratégie d'impression de la table : aucun type en dur hors
// des stratégies 'sum', 'product', 'power', 'prefix', 'infix', 'template' (Div → fraction), 'call'.
export function astToTokens(n, table, parentPrec = 0) {
  const spec = table.nodes[n.t];
  const prec = n.t === 'Num' || n.t === 'Sym' ? 100 : (spec?.prec ?? 100);
  const group = (toks, need) => (need ? [{ k: 'lp' }, ...toks, { k: 'rp' }] : toks);
  if (n.t === 'Num') {
    const neg = n.p < 0,
      a = Math.abs(n.p);
    let body;
    if (n.big) body = [{ k: 'num', v: String(n.big).replace(/^[-−]/, '') }];
    else if (n.dec) body = [{ k: 'num', v: n.dec.replace(/^[-−]/, '') }];
    else if (n.q === 1) body = [{ k: 'num', v: String(a) }];
    else
      body = [
        { k: 'frac', num: [{ k: 'num', v: String(a) }], den: [{ k: 'num', v: String(n.q) }] },
      ];
    return neg ? group([{ k: 'op', v: '−' }, ...body], parentPrec > 21) : body;
  }
  if (n.t === 'Sym') return [{ k: 'id', v: n.name }];
  const kid = (c, p) => astToTokens(c, table, p);
  let out;
  switch (spec?.print) {
    case 'sum':
      out = n.k.flatMap((c, i) => {
        if (i === 0) return kid(c, prec);
        if (c.t === 'Neg') return [{ k: 'op', v: '−' }, ...kid(c.k[0], prec + 0.5)];
        if (c.t === 'Num' && c.p < 0)
          return [
            { k: 'op', v: '−' },
            ...kid({ ...c, p: -c.p, dec: c.dec?.replace(/^[-−]/, '') }, prec + 0.5),
          ];
        return [{ k: 'op', v: '+' }, ...kid(c, prec)];
      });
      break;
    case 'product':
      out = n.k.flatMap((c, i) => {
        const neg = c.t === 'Neg' || (c.t === 'Num' && c.p < 0);
        const part = i && neg ? group(kid(c, 0), true) : kid(c, prec);
        if (i === 0) return part;
        const a = n.k[i - 1];
        const leftOk = (a.t === 'Num' && a.p > 0 && a.q === 1 && !a.dec) || a.t === 'Sym';
        const grouped =
          (table.nodes[c.t]?.prec ?? 100) < prec && table.nodes[c.t]?.eval !== 'div' && !neg;
        // l'infini (« +∞ ») n'est pas une lettre : « 3 × +∞ », jamais « 3 +∞ » (relu comme une somme)
        const letterSym = c.t === 'Sym' && /^\p{L}/u.test(c.name);
        // deux noms dont l'un a plusieurs lettres (MN, AB) : « MN × AB », jamais « MNAB » (illisible)
        const rightSym = c.t === 'Sym' ? c : c.t === 'Pow' && c.k[0].t === 'Sym' ? c.k[0] : null;
        const longNames =
          a.t === 'Sym' && rightSym && (isLongName(a.name) || isLongName(rightSym.name));
        // grandeurs (feuille « unités ») : un nombre puis son unité s'écrivent collés (« 2,35 € », « 45 min »)
        const unitAfterNumber =
          c.t === 'Sym' && table.unitNames?.has(c.name) && a.t === 'Num' && a.p > 0;
        const juxta =
          grouped ||
          unitAfterNumber ||
          (leftOk &&
            !longNames &&
            ((letterSym && !(a.t === 'Sym' && a.name === c.name)) ||
              (c.t === 'Pow' && c.k[0].t === 'Sym')));
        return juxta ? part : [{ k: 'op', v: '×' }, ...part];
      });
      break;
    case 'power': {
      const [b, e] = n.k;
      // une racine, une valeur absolue… élevée à une puissance s'écrit entre parenthèses : (√a)², jamais √(a)²
      const wrapBase = ['sqrt', 'abs', 'fact', 'percent'].includes(table.nodes[b.t]?.eval);
      const base = wrapBase ? group(kid(b, 0), true) : kid(b, prec + 0.5);
      if (e.t === 'Num' && e.q === 1 && (e.p === 2 || e.p === 3))
        out = [...base, { k: 'op', v: e.p === 2 ? '²' : '³' }];
      else out = [...base, { k: 'op', v: '^' }, { k: 'lp' }, ...kid(e, 0), { k: 'rp' }];
      break;
    }
    case 'prefix':
      out = [{ k: 'op', v: '−' }, ...kid(n.k[0], spec.operandPrec ?? prec)];
      break;
    case 'infix':
      out = n.k.flatMap((c, i) =>
        i ? [{ k: 'op', v: spec.text.trim() }, ...kid(c, prec + 0.5)] : kid(c, prec),
      );
      break;
    case 'call':
      out = [
        { k: 'id', v: n.name },
        { k: 'lp' },
        ...n.k.flatMap((c, i) => (i ? [{ k: 'op', v: ';' }, ...kid(c, 0)] : kid(c, 0))),
        { k: 'rp' },
      ];
      break;
    default:
      if (spec?.eval === 'div') {
        // le signe reste à gauche de la fraction : −2/3 → « − » puis 2/3
        let [top, bot] = n.k,
          neg = false;
        if (top.t === 'Num' && top.p < 0) {
          top = { ...top, p: -top.p, dec: top.dec?.replace(/^[-−]/, '') };
          neg = true;
        } else if (table.nodes[top.t]?.print === 'prefix') {
          top = top.k[0];
          neg = true;
        }
        const frac = { k: 'frac', num: kid(top, 0), den: kid(bot, 0) };
        return neg ? group([{ k: 'op', v: '−' }, frac], parentPrec > 21) : [frac];
      }
      if (spec?.eval === 'sqrt')
        return [{ k: 'op', v: '√' }, { k: 'lp' }, ...kid(n.k[0], 0), { k: 'rp' }];
      // dérivée (u)′ : toujours entre parenthèses, la marque après
      if (spec?.eval === 'derive')
        return [
          { k: 'lp' },
          ...kid(n.k[0], 0),
          { k: 'rp' },
          { k: 'op', v: table.lex?.prime ?? '′' },
        ];
      // côté d'approche « 3⁺ », « 0⁻ » : le nombre, puis la marque (texte du nœud après $0)
      if (spec?.eval === 'right' || spec?.eval === 'left')
        return [...kid(n.k[0], 100), { k: 'op', v: spec.text.replace('$0', '') }];
      // factorielle « 8! », « (n + 1)! » : l'argument (entre parenthèses s'il n'est pas simple), puis « ! »
      if (spec?.eval === 'fact') return [...kid(n.k[0], 100), { k: 'op', v: '!' }];
      // degrés « 35° » : le nombre, puis « ° » (sans lui, tan(35°) se relirait en radians)
      if (spec?.eval === 'deg') return [...kid(n.k[0], 100), { k: 'op', v: '°' }];
      // nœud écrit par un gabarit (« $0 parmi $1 », « |$0| », « $0 % ») : les mots et les signes du gabarit
      // sont des jetons, les places $i les enfants
      if (spec?.print === 'template' && typeof spec.text === 'string') {
        const pieces = spec.text.split(/(\$\d)/);
        const enclosed = /^\W*\$0\W*$/u.test(spec.text) && spec.text.trim()[0] !== '$';
        out = pieces.flatMap((piece) => {
          const m = /^\$(\d)$/.exec(piece);
          if (m) return kid(n.k[Number(m[1])], enclosed ? 0 : prec + 0.5);
          const w = piece.trim();
          return w ? [{ k: 'op', v: w }] : [];
        });
        break;
      }
      out = n.k.flatMap((c) => kid(c, prec));
  }
  return group(out, prec < parentPrec);
}

export { samePath };

// ── Structure d'une ligne pour la sélection progressive ───────────────────
// Analyse de précédence sur les jetons d'UN conteneur : chaque nœud est une plage [from, to) avec, pour une
// opération, l'indice de son opérateur. Sert à : double-clic sur un opérateur → son opération ; clic sur
// une sélection → l'opération qui l'englobe. (Grammaire de l'écriture, pas de calcul.)
const PREC = { rel: 1, add: 2, mul: 3 };
export function spanTree(arr) {
  let i = 0;
  const nodes = [];
  const mk = (from, to, op = null, kids = []) => {
    const n = { from, to, op, kids };
    nodes.push(n);
    return n;
  };
  const level = (t) =>
    t?.k !== 'op'
      ? 0
      : RELS.has(t.v)
        ? PREC.rel
        : BIN_ADD.has(t.v)
          ? PREC.add
          : BIN_MUL.has(t.v)
            ? PREC.mul
            : 0;
  const startsUnit = (t) => t && (t.k === 'num' || t.k === 'id' || t.k === 'frac' || t.k === 'lp');
  function atom() {
    const t = arr[i];
    if (!t) return null;
    if (t.k === 'op' && (t.v === '−' || t.v === '+') && isUnary(arr, i)) {
      const s = i++;
      const a = postfix();
      return mk(s, a ? a.to : i, s, a ? [a] : []);
    }
    if (t.k === 'op' && t.v === '√') {
      const s = i++;
      const a = postfix();
      return mk(s, a ? a.to : i, s, a ? [a] : []);
    }
    if (t.k === 'lp') {
      const s = i++;
      const inner = expr(0);
      if (arr[i]?.k === 'rp') i++;
      return mk(s, i, null, inner ? [inner] : []);
    }
    if (t.k === 'num' || t.k === 'id' || t.k === 'frac') {
      i++;
      return mk(i - 1, i);
    }
    i++;
    return mk(i - 1, i);
  }
  function postfix() {
    let a = atom();
    while (a && arr[i]?.k === 'op' && (POSTFIX.has(arr[i].v) || arr[i].v === '^')) {
      const opAt = i++;
      if (arr[opAt].v === '^') {
        const e = atom();
        a = mk(a.from, e ? e.to : i, opAt, e ? [a, e] : [a]);
      } else a = mk(a.from, i, opAt, [a]);
    }
    return a;
  }
  function expr(min) {
    let left = postfix();
    for (;;) {
      const t = arr[i];
      const lv = level(t);
      if (lv && lv > min && !(BIN_ADD.has(t.v) && isUnary(arr, i))) {
        const opAt = i++;
        const right = expr(lv);
        left = mk(left.from, right ? right.to : i, opAt, right ? [left, right] : [left]);
        continue;
      }
      if (startsUnit(t) && PREC.mul > min) {
        // produit implicite : 3x, (x + 1)(x − 2)
        const right = expr(PREC.mul);
        left = mk(left.from, right.to, null, [left, right]);
        continue;
      }
      return left;
    }
  }
  while (i < arr.length) {
    const before = i;
    expr(0);
    if (i === before) i++;
  }
  return nodes;
}

// Double-clic sur un opérateur : l'opération dont il est l'opérateur (avec ses deux opérandes).
export function selectOperation(state, c, i) {
  const arr = container(state, c);
  const t = arr[i];
  if (t?.k === 'lp' || t?.k === 'rp') return selectSmart(state, c, i);
  const node = spanTree(arr)
    .filter((n) => n.op === i)
    .sort((a, b) => a.to - a.from - (b.to - b.from))[0];
  if (node) select(state, c, node.from, node.to);
  else selectSmart(state, c, i);
}

// Clic sur la sélection : l'opération qui l'englobe ; au bout du conteneur, on remonte à la fraction parente.
export function expandSelection(state) {
  const s = state.sel;
  if (!s) return false;
  const arr = container(state, s.c);
  const bigger = spanTree(arr)
    .filter((n) => n.from <= s.from && n.to >= s.to && n.to - n.from > s.to - s.from)
    .sort((a, b) => a.to - a.from - (b.to - b.from))[0];
  if (bigger) {
    select(state, s.c, bigger.from, bigger.to);
    return true;
  }
  if (s.from === 0 && s.to === arr.length && s.c.length) {
    const parent = s.c.slice(0, -2),
      idx = s.c[s.c.length - 2];
    select(state, parent, idx, idx + 1);
    return true;
  }
  if (!(s.from === 0 && s.to === arr.length)) {
    select(state, s.c, 0, arr.length);
    return true;
  }
  return false;
}

// Remplace la sélection par des jetons (résultat d'un calcul fait par le moteur). Annulable.
export function replaceSelection(state, tokens) {
  const s = state.sel;
  if (!s) return false;
  snapshot(state);
  const arr = container(state, s.c);
  let from = s.from,
    to = s.to;
  // écriture : « (2 × 5) » calculé en 10 → « 10 », pas « (10) » (les parenthèses n'entourent plus rien)
  const single = tokens.length === 1 && tokens[0].k !== 'op';
  if (
    single &&
    arr[from - 1]?.k === 'lp' &&
    arr[to]?.k === 'rp' &&
    matchParen(arr, from - 1) === to
  ) {
    from--;
    to++;
  }
  arr.splice(from, to - from, ...clone(tokens));
  select(state, s.c, from, from + tokens.length);
  return true;
}

// ── Commutativité : déplacer un terme d'une somme ou un facteur d'un produit (glisser-déposer) ──
// Grammaire de l'écriture, pas de calcul : on lit la somme / le produit qui contient le jeton, on le
// découpe en opérandes (avec leur signe pour une somme), on réordonne, on réécrit les jetons.
const isBinAddAt = (arr, j) => arr[j]?.k === 'op' && BIN_ADD.has(arr[j].v) && !isUnary(arr, j);
function kindOf(arr, n) {
  if (!n || n.kids.length !== 2) return null;
  if (n.op != null && isBinAddAt(arr, n.op)) return 'sum';
  if (
    (n.op != null && arr[n.op]?.k === 'op' && arr[n.op].v === '×') ||
    (n.op == null && arr[n.from]?.k !== 'lp')
  )
    return 'prod';
  if (n.op == null && n.kids.length === 2 && n.kids[0].to <= n.kids[1].from) return 'prod'; // (a)(b)
  return null;
}
// Le groupe (somme ou produit) le plus proche qui contient l'indice i, et ses opérandes.
export function operandGroup(state, c, i, prefer = null) {
  const arr = container(state, c);
  const nodes = spanTree(arr);
  const parentOf = (n) =>
    nodes.filter((m) => m !== n && m.from <= n.from && m.to >= n.to && m.kids.includes(n))[0] ||
    null;
  let n = nodes
    .filter(
      (m) => m.from <= i && i < m.to && kindOf(arr, m) && (!prefer || kindOf(arr, m) === prefer),
    )
    .sort((a, b) => a.to - a.from - (b.to - b.from))[0];
  if (!n) return null;
  const kind = kindOf(arr, n);
  for (let p = parentOf(n); p && kindOf(arr, p) === kind; p = parentOf(p)) n = p; // la chaîne entière : a + b + c
  const leaves = [];
  const flat = (m) => {
    if (kindOf(arr, m) === kind && m.kids.length === 2) {
      flat(m.kids[0]);
      flat(m.kids[1]);
    } else leaves.push(m);
  };
  flat(n);
  const ops = leaves.map((m, k) => {
    if (kind === 'sum') {
      let sign = '+',
        from = m.from;
      if (k > 0 && isBinAddAt(arr, m.from - 1)) {
        sign = arr[m.from - 1].v;
        from = m.from - 1;
      }
      let body = [m.from, m.to];
      if (k === 0 && arr[m.from]?.k === 'op' && arr[m.from].v === '−' && isUnary(arr, m.from)) {
        sign = '−';
        body = [m.from + 1, m.to];
      }
      return { from, to: m.to, sign, body };
    }
    return { from: m.from, to: m.to, body: [m.from, m.to] };
  });
  return { c, kind, from: n.from, to: n.to, ops };
}
// L'opérande qui contient l'indice i (ou la sélection), dans son groupe.
export function operandAt(group, i) {
  return group ? group.ops.findIndex((o) => o.from <= i && i < o.to) : -1;
}

// Déplace l'opérande `k` à la place `slot` (0 = en tête, ops.length = à la fin). Annulable.
export function moveOperand(state, group, k, slot) {
  if (!group || k < 0 || k >= group.ops.length) return false;
  const target = slot > k ? slot - 1 : slot;
  if (target === k) return false;
  snapshot(state);
  const arr = container(state, group.c);
  const parts = group.ops.map((o) => ({ ...o, toks: clone(arr.slice(o.body[0], o.body[1])) }));
  const [moved] = parts.splice(k, 1);
  parts.splice(target, 0, moved);
  const out = [];
  let selFrom = 0,
    selTo = 0;
  parts.forEach((p, j) => {
    const start = out.length;
    if (group.kind === 'sum') {
      if (j === 0) {
        if (p.sign === '−') out.push({ k: 'op', v: '−' });
      } else out.push({ k: 'op', v: p.sign });
    } else if (j > 0) out.push({ k: 'op', v: '×' });
    out.push(...p.toks);
    if (p === moved) {
      selFrom = start + (group.kind === 'prod' && j > 0 ? 1 : 0);
      selTo = out.length;
    }
  });
  arr.splice(group.from, group.to - group.from, ...out);
  select(state, group.c, group.from + selFrom, group.from + selTo);
  return true;
}
// Le groupe dont un opérande coïncide avec la plage sélectionnée (signe compris ou non) : c'est ce qu'on glisse.
export function operandGroupForSelection(state) {
  const s = state.sel;
  if (!s) return null;
  const arr = container(state, s.c);
  for (const prefer of ['sum', 'prod']) {
    for (let i = s.from; i < s.to; i++) {
      const g = operandGroup(state, s.c, i, prefer);
      const k = g
        ? g.ops.findIndex((o) => (o.from === s.from || o.body[0] === s.from) && o.to === s.to)
        : -1;
      if (k >= 0) return { group: g, k };
    }
  }
  return null;
}

// Insère des jetons au curseur (un morceau de l'énoncé). Annulable.
export function insertTokens(state, tokens) {
  snapshot(state);
  const { c, i } = state.cursor;
  const arr = container(state, c);
  arr.splice(i, 0, ...clone(tokens));
  state.sel = null;
  state.cursor = { c, i: i + tokens.length };
}

// ── Enlever des parenthèses précédées d'un signe ──────────────────────────
// Geste « enlever les parenthèses » : « − (x + 1) » ou « + (x − 1) » perd ses parenthèses et son signe, et
// chaque terme reçoit une case de signe VIDE { k:'sign' }. L'élève remplit chaque case (+ ou −) ; le juge
// tranche ensuite comme pour toute ligne. Le geste est le même pour + et − : il ne dit pas la règle.
const MUL_LIKE = (t) =>
  t &&
  ((t.k === 'op' && (BIN_MUL.has(t.v) || POSTFIX.has(t.v) || t.v === '^')) ||
    ['num', 'id', 'frac', 'lp'].includes(t.k));
// Le groupe désigné par la sélection : le signe devant « ( », une parenthèse, ou tout le bloc « −(…) ».
// → { sign, lp, rp } (sign = indice du signe, ou null pour un groupe en tête), ou null si le geste ne s'applique pas
export function parenGroupAt(arr, from, to = from + 1) {
  let lp = -1;
  const t = arr[from];
  if (t?.k === 'op' && BIN_ADD.has(t.v) && arr[from + 1]?.k === 'lp') lp = from + 1;
  else if (t?.k === 'lp' || t?.k === 'rp') lp = t.k === 'lp' ? from : matchParen(arr, from);
  if (lp < 0) return null;
  const rp = matchParen(arr, lp);
  if (rp < 0) return null;
  const sign = lp > 0 && arr[lp - 1].k === 'op' && BIN_ADD.has(arr[lp - 1].v) ? lp - 1 : null;
  if (to - from > 1 && !(from === (sign ?? lp) && to === rp + 1)) return null; // sélection plus large : pas ce geste
  const before = arr[(sign ?? lp) - 1];
  if (before?.k === 'op' && (BIN_MUL.has(before.v) || before.v === '^')) return null; // facteur d'un produit
  if (sign == null && before && before.k !== 'lp' && !(before.k === 'op' && RELS.has(before.v)))
    return null;
  if (MUL_LIKE(arr[rp + 1])) return null; // (…)×…, (…)², (…)(…)
  if (rp - lp < 2) return null;
  return { sign, lp, rp };
}
export function removeParens(state, c, g) {
  const arr = container(state, c);
  const inner = arr.slice(g.lp + 1, g.rp);
  const terms = [];
  let d = 0,
    cur = [];
  inner.forEach((t, j) => {
    if (t.k === 'lp') d++;
    else if (t.k === 'rp') d--;
    if (
      d === 0 &&
      t.k === 'op' &&
      BIN_ADD.has(t.v) &&
      (cur.length || j === 0) &&
      !(cur.length && isUnary(inner, j))
    ) {
      if (cur.length) terms.push(cur);
      cur = [];
      return; // le signe d'origine disparaît : l'élève le rechoisit
    }
    cur.push(t);
  });
  if (cur.length) terms.push(cur);
  if (!terms.length) return false;
  snapshot(state);
  const out = terms.flatMap((tt) => [{ k: 'sign' }, ...clone(tt)]);
  const start = g.sign ?? g.lp;
  arr.splice(start, g.rp + 1 - start, ...out);
  state.sel = null;
  state.cursor = { c, i: start };
  return true;
}
// Remplit la prochaine case de signe ; un « + » en tête (début, après « ( » ou une relation) ne s'écrit pas.
export function fillSign(state, v) {
  const slot = emptySlot(state.tokens);
  if (!slot?.sign) return false;
  snapshot(state);
  const arr = container(state, slot.path);
  const prev = arr[slot.i - 1];
  const lead = !prev || prev.k === 'lp' || (prev.k === 'op' && !POSTFIX.has(prev.v));
  if (v === '+' && lead) arr.splice(slot.i, 1);
  else arr[slot.i] = { k: 'op', v };
  state.sel = null;
  const next = emptySlot(state.tokens);
  // tant qu'il reste une case, le curseur la désigne ; la dernière remplie, il repart après l'expression (fin de ligne,
  // hors de l'exposant ou de la fraction) : l'élève reprend l'écriture là où il l'avait laissée
  state.cursor = next?.sign ? { c: next.path, i: next.i } : { c: [], i: state.tokens.length };
  return true;
}

// ── Opérandes voisins qui s'annulent (a − a, a × 1/a) ─────────────────────
// Toutes les paires d'opérandes VOISINS des sommes et produits de la ligne, fractions comprises, avec leur
// texte pour le moteur : c'est lui qui dit si la paire s'annule (outils de genre neutral), pas l'éditeur.
// → [{ c, group, k, from, to, a, b }] : la paire = opérandes k et k + 1, plage [from, to) à souligner.
export function operandPairs(state) {
  const out = [];
  const seen = new Set();
  const walk = (arr, c) => {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i].k === 'frac') {
        walk(arr[i].num, [...c, i, 'num']);
        walk(arr[i].den, [...c, i, 'den']);
      }
      for (const kind of ['sum', 'prod']) {
        const g = operandGroup(state, c, i, kind);
        if (!g || g.kind !== kind) continue;
        const id = JSON.stringify([c, kind, g.from, g.to]);
        if (seen.has(id)) continue;
        seen.add(id);
        const text = (o) => {
          const body = toEngineText(arr.slice(o.body[0], o.body[1]));
          return kind === 'sum' ? (o.sign === '−' ? `− ${body}` : body) : body;
        };
        for (let k = 0; k + 1 < g.ops.length; k++) {
          const A = g.ops[k],
            B = g.ops[k + 1];
          out.push({
            c,
            group: g,
            k,
            from: A.from,
            to: B.to,
            a: text(A),
            b:
              kind === 'sum'
                ? `${B.sign} ${toEngineText(arr.slice(B.body[0], B.body[1]))}`
                : text(B),
          });
        }
      }
    }
  };
  walk(state.tokens, []);
  return out;
}
// Supprime les opérandes k et k + 1 du groupe (mode 'remove'), ou les remplace par l'élément neutre
// (mode 'identity' : 0 pour une somme, 1 pour un produit). Un groupe vidé devient l'élément neutre. Annulable.
export function cancelPair(state, group, k, mode, identity) {
  if (!group || k < 0 || k + 1 >= group.ops.length) return false;
  return removeOps(state, group, k, k + 1, mode === 'identity' ? identity : null, identity);
}
// Les termes k0..k1 de la somme (ou facteurs du produit) sélectionnés, s'ils couvrent exactement la sélection.
export function selectedOperands(state) {
  const s = state.sel;
  if (!s) return null;
  for (let i = s.from; i < s.to; i++) {
    const g = operandGroup(state, s.c, i, 'sum');
    if (!g || g.kind !== 'sum') continue;
    const k0 = g.ops.findIndex((o) => o.from === s.from || o.body[0] === s.from);
    const k1 = g.ops.findIndex((o) => o.to === s.to);
    if (k0 >= 0 && k1 >= k0) return { group: g, k0, k1 };
  }
  return null;
}
// « Terme vide » : efface les termes sélectionnés d'une somme, signes compris (une somme vidée devient 0). Annulable.
export function eraseTerms(state) {
  const r = selectedOperands(state);
  if (!r) return false;
  return removeOps(state, r.group, r.k0, r.k1, null, '0');
}
function removeOps(state, group, k0, k1, replaceBy, identity) {
  snapshot(state);
  const arr = container(state, group.c);
  const parts = group.ops.map((o) => ({
    sign: o.sign || '+',
    toks: clone(arr.slice(o.body[0], o.body[1])),
  }));
  const neutral = { sign: '+', toks: [{ k: 'num', v: replaceBy ?? identity }] };
  parts.splice(k0, k1 - k0 + 1, ...(replaceBy != null ? [neutral] : []));
  if (!parts.length) parts.push({ sign: '+', toks: [{ k: 'num', v: identity }] });
  const out = [];
  parts.forEach((p, j) => {
    if (group.kind === 'sum') {
      if (j === 0) {
        if (p.sign === '−') out.push({ k: 'op', v: '−' });
      } else out.push({ k: 'op', v: p.sign });
    } else if (j > 0) out.push({ k: 'op', v: '×' });
    out.push(...p.toks);
  });
  arr.splice(group.from, group.to - group.from, ...out);
  state.sel = null;
  state.cursor = { c: group.c, i: group.from + out.length };
  return true;
}

// ── Outil « Remplacer » : chaque lettre `name` de la ligne devient la valeur choisie ─────────────
// Parenthèses quand il le faut : valeur négative, fraction ou calcul (2x² avec x = −1 → 2(−1)²), sauf si la lettre
// est déjà seule entre parenthèses (f(x) → f(−1)) ; un « × » devant un nombre collé (2x avec x = 3 → 2 × 3).
export function substitute(state, name, valueTokens) {
  const simple = valueTokens.length === 1 && valueTokens[0].k === 'num';
  let n = 0;
  const walk = (arr) => {
    for (let i = 0; i < arr.length; i++) {
      const t = arr[i];
      if (t.k === 'frac') {
        walk(t.num);
        walk(t.den);
        continue;
      }
      if (t.k !== 'id' || t.v !== name) continue;
      const prev = arr[i - 1],
        next = arr[i + 1];
      const alone = prev?.k === 'lp' && next?.k === 'rp';
      let rep = clone(valueTokens);
      if (!simple && !alone) rep = [{ k: 'lp' }, ...rep, { k: 'rp' }];
      else if (
        simple &&
        prev &&
        (prev.k === 'num' ||
          prev.k === 'id' ||
          prev.k === 'rp' ||
          (prev.k === 'op' && POSTFIX.has(prev.v)))
      )
        rep = [{ k: 'op', v: '×' }, ...rep];
      if (simple && next && (next.k === 'num' || next.k === 'id' || next.k === 'lp'))
        rep = [...rep, { k: 'op', v: '×' }];
      arr.splice(i, 1, ...rep);
      i += rep.length - 1;
      n++;
    }
  };
  const before = clone({ tokens: state.tokens, cursor: state.cursor, sel: state.sel });
  walk(state.tokens);
  if (!n) return 0;
  state.undo.push(before);
  state.sel = null;
  state.cursor = { c: [], i: state.tokens.length };
  return n;
}
// la lettre sélectionnée (un seul jeton lettre), sinon null
export function selectedLetter(state) {
  if (!state.sel || state.sel.to - state.sel.from !== 1) return null;
  const t = container(state, state.sel.c)[state.sel.from];
  return t?.k === 'id' ? t.v : null;
}

// ── Les morceaux de la sélection : viser gros du doigt, préciser au clavier ──
// Les constituants DIRECTS du bloc sélectionné — « 3² + 4² » → « 3² » · « 4² », « 3x » → « 3 » · « x »,
// « (x + 1) » → « x + 1 ». Jamais plus d'un cran : les enfants, pas les petits-enfants. Un seul jeton,
// ou rien de plus petit à l'intérieur → aucun morceau.
// ── Passer un terme de l'autre côté du signe égal (clavier v3) ────────────
// L'éditeur ne décide JAMAIS de l'opération : il écrit le terme à sa future place dans l'autre membre et
// laisse une case de signe (`{k:'sign', cross:true}`) que l'élève remplit. C'est le moteur qui jugera la
// ligne — « passer de l'autre côté » y redevient « retrancher 5 des deux membres ».

/** l'index, au niveau racine, de la relation de la ligne (une seule) ; -1 sinon */
function rootRelation(arr) {
  let d = 0;
  let at = -1;
  for (let i = 0; i < arr.length; i++) {
    const t = arr[i];
    if (t.k === 'lp') d++;
    else if (t.k === 'rp') d--;
    else if (d === 0 && t.k === 'op' && RELS.has(t.v)) {
      if (at >= 0) return -1; // deux relations (encadrement, « ou ») : on ne touche pas
      at = i;
    }
  }
  return at;
}

/**
 * La plage à retirer pour sortir l'opérande k de son groupe : son signe (somme) ou son « × » (produit).
 * Une division n'est jamais un groupe pour l'éditeur (`kindOf` ne connaît que « × ») : « x ÷ 3 = 5 » est
 * donc hors périmètre du passage de l'autre côté, comme du glissé.
 */
function operandCut(arr, group, k) {
  const o = group.ops[k];
  if (group.kind === 'sum') return [o.from, o.to];
  const before = o.from - 1;
  if (k > 0 && arr[before]?.k === 'op' && arr[before].v === '×') return [before, o.to];
  if (k === 0 && arr[o.to]?.k === 'op' && arr[o.to].v === '×') return [o.from, o.to + 1];
  return [o.from, o.to]; // produit implicite (3x) : il n'y a rien d'autre à retirer
}

/**
 * Le passage permis pour la sélection, s'il y en a un. Permis pour un terme d'une somme au niveau racine
 * d'un membre, un facteur d'un produit explicite ou implicite qui occupe tout le membre — et seulement si
 * le membre d'origine ne se vide pas.
 * → { side: 'left' | 'right', kind: 'sum' | 'prod', text, group, k } ou null
 */
export function crossOption(state) {
  const sel = state.sel;
  if (!sel || sel.c.length || emptySlot(state.tokens)) return null;
  const arr = state.tokens;
  const rel = rootRelation(arr);
  if (rel < 0) return null;
  const toRight = sel.to <= rel;
  if (!toRight && sel.from <= rel) return null; // la sélection chevauche la relation
  const memberFrom = toRight ? 0 : rel + 1;
  const memberTo = toRight ? rel : arr.length;
  const g = operandGroupForSelection(state);
  if (!g || g.group.c.length) return null;
  const { group, k } = g;
  // le groupe doit occuper tout le membre : sinon « passer de l'autre côté » ne vaut pas pour la ligne
  if (group.from !== memberFrom || group.to !== memberTo) return null;
  if (group.ops.length < 2) return null;
  const o = group.ops[k];
  return {
    side: toRight ? 'right' : 'left',
    kind: group.kind,
    text: toEngineText(arr.slice(o.body[0], o.body[1])).trim(),
    group,
    k,
  };
}

/** Le morceau est-il une somme (un + ou un − binaire à son niveau) ? Un facteur qui y arrive se parenthèse. */
function isSumSpan(arr, from, to) {
  let depth = 0;
  for (let i = from; i < to; i++) {
    const t = arr[i];
    if (t.k === 'lp') depth++;
    else if (t.k === 'rp') depth--;
    else if (!depth && t.k === 'op' && BIN_ADD.has(t.v) && !isUnary(arr, i)) return true;
  }
  return false;
}

/** Écrit le terme sélectionné à sa future place dans l'autre membre, derrière une case de signe. Annulable. */
export function crossEquals(state) {
  const opt = crossOption(state);
  if (!opt) return false;
  const arr = state.tokens;
  const rel = rootRelation(arr);
  const { group, k } = opt;
  const o = group.ops[k];
  const term = clone(arr.slice(o.body[0], o.body[1]));
  const cut = operandCut(arr, group, k);
  const ins = [{ k: 'sign', cross: true }, ...term];
  snapshot(state);
  // un FACTEUR qui arrive dans une somme : le membre d'arrivée se parenthèse, sinon « 3x = y + 6 » donnerait
  // « x = y + 6 ÷ 3 », qui est faux. Les parenthèses d'abord : les repères du membre d'origine glissent.
  let shift = 0;
  let insertAt = opt.side === 'right' ? arr.length : rel;
  if (opt.kind === 'prod') {
    const from = opt.side === 'right' ? rel + 1 : 0;
    const to = opt.side === 'right' ? arr.length : rel;
    if (isSumSpan(arr, from, to)) {
      arr.splice(to, 0, { k: 'rp' });
      arr.splice(from, 0, { k: 'lp' });
      if (opt.side === 'right') insertAt = arr.length;
      else {
        // le membre d'origine est à droite de la relation : il a glissé de deux jetons
        shift = 2;
        insertAt = rel + 2;
      }
    }
  }
  cut[0] += shift;
  cut[1] += shift;
  if (insertAt >= cut[1]) {
    arr.splice(insertAt, 0, ...ins);
    arr.splice(cut[0], cut[1] - cut[0]);
  } else {
    arr.splice(cut[0], cut[1] - cut[0]);
    arr.splice(insertAt, 0, ...ins);
  }
  // un « + » resté en tête du membre d'origine ne s'écrit pas
  const rel2 = rootRelation(arr);
  const srcStart = opt.side === 'right' ? 0 : rel2 + 1;
  if (arr[srcStart]?.k === 'op' && arr[srcStart].v === '+' && isUnary(arr, srcStart))
    arr.splice(srcStart, 1);
  state.sel = null;
  const slot = emptySlot(state.tokens);
  state.cursor = slot ? { c: slot.path, i: slot.i } : { c: [], i: state.tokens.length };
  return true;
}

export function subParts(state) {
  const s = state.sel;
  if (!s) return [];
  const arr = container(state, s.c);
  const span = s.to - s.from;
  if (span <= 1) return [];
  // entièrement parenthésée : le morceau, c'est ce qu'il y a dedans
  if (arr[s.from]?.k === 'lp' && matchParen(arr, s.from) === s.to - 1)
    return span > 2 ? [{ c: clone(s.c), from: s.from + 1, to: s.to - 1 }] : [];
  const node = spanTree(arr).find((n) => n.from === s.from && n.to === s.to);
  return (node?.kids ?? [])
    .filter((k) => k.to - k.from < span)
    .map((k) => ({ c: clone(s.c), from: k.from, to: k.to }));
}

// ── Aide à la sélection (le doigt est imprécis) : la sélection, puis ce qui l'englobe, niveau par niveau ──
// → [{ c, from, to }] (la sélection actuelle d'abord, au plus `max` niveaux) ; mêmes crans que expandSelection.
export function selectionLadder(state, max = 4) {
  if (!state.sel) return [];
  const probe = {
    tokens: state.tokens,
    sel: clone(state.sel),
    cursor: clone(state.cursor),
    undo: [],
  };
  const out = [clone(probe.sel)];
  while (out.length < max && expandSelection(probe)) {
    const s = clone(probe.sel);
    if (out.some((x) => samePath(x.c, s.c) && x.from === s.from && x.to === s.to)) break;
    out.push(s);
  }
  return out;
}
